import {
  Home,
  Calendar,
  Users,
  Settings,
  Trophy,
  LogOut,
} from "lucide-react";
import { useLocation } from "wouter";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase/client";

export function ClubSidebar() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();

  const menuItems = [
    {
      title: "Inicio",
      url: "/club/dashboard",
      icon: Home,
      visible: true
    },
    {
      title: "Mis Canchas",
      url: "/club/mis-canchas",
      icon: Trophy,
      visible: true
    },
    {
      title: "Ligas",
      url: "/club/ligas",
      icon: Trophy,
      visible: true
    },
    {
      title: "Reservaciones",
      url: "/club/reservaciones",
      icon: Calendar,
      visible: true
    },
    {
      title: "Mi Perfil",
      url: "/club/perfil",
      icon: Users,
      visible: true
    }
  ];

  const getInitials = (name?: string | null) => {
    if (!name) return 'C';
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + (words[words.length - 1][0] || '')).toUpperCase();
  };

  return (
    <div 
      data-testid="club-sidebar"
      style={{
        width: '260px',
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 16px',
        borderRight: '1px solid rgba(255,255,255,0.06)'
      }}
    >
      {/* Logo Next Point */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
          <img 
            src="/logo.png" 
            alt="Next Point" 
            style={{ height: '56px', width: 'auto' }} 
          />
        </div>

        {/* Avatar y nombre del club */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', marginBottom: '12px' }}>
            <Avatar style={{ width: '100%', height: '100%' }}>
              <AvatarImage src={user?.profileImageUrl || undefined} />
              <AvatarFallback style={{
                background: 'linear-gradient(to bottom right, #10b981, #059669)',
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                {getInitials(user?.clubName || user?.fullName)}
              </AvatarFallback>
            </Avatar>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <p style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '18px', margin: '0 0 4px 0' }}>
              {user?.clubName || user?.fullName || 'Mi Club'}
            </p>
            <div style={{
              backgroundColor: '#10b981',
              color: '#000000',
              fontSize: '12px',
              fontWeight: '500',
              padding: '4px 8px',
              borderRadius: '9999px',
              display: 'inline-block'
            }}>
              Club
            </div>
          </div>
        </div>
      </div>

      {/* Navegación */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {menuItems.filter(item => item.visible).map((item) => (
          <a
            key={item.url}
            href={item.url}
            data-testid={`menu-${item.title.toLowerCase().replace(' ', '-')}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'all 200ms ease',
              backgroundColor: location === item.url ? '#10b981' : 'transparent',
              color: location === item.url ? '#ffffff' : '#94a3b8'
            }}
            onMouseEnter={(e) => {
              if (location !== item.url) {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (location !== item.url) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }
            }}
          >
            <item.icon style={{ width: '20px', height: '20px' }} />
            <span style={{ fontWeight: '500' }}>{item.title}</span>
          </a>
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px', marginTop: '16px' }}>
        <button
          onClick={async () => {
            console.log('🚨 CLUB SIDEBAR LOGOUT CALLED');
            try {
              // Get token directly from localStorage to bypass cache issues
              let accessToken: string | null = null;
              
              try {
                const sessionData = localStorage.getItem('supabase.auth.token');
                if (sessionData) {
                  const parsed = JSON.parse(sessionData);
                  accessToken = parsed?.access_token || null;
                }
              } catch (e) {
                console.log('Could not get token from localStorage:', e);
              }
              
              // Fallback to getSession if localStorage fails
              if (!accessToken) {
                const { data: { session } } = await supabase.auth.getSession();
                accessToken = session?.access_token || null;
              }
              
              console.log('🔐 Club Sidebar Logout - Token available:', !!accessToken);
              
              // Call backend logout endpoint with token if available
              const headers: Record<string, string> = {
                'Content-Type': 'application/json',
              };
              
              if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
                console.log('✅ Token added to club sidebar logout headers');
              } else {
                console.log('❌ No token available for club sidebar logout');
              }
              
              await fetch('/api/auth/logout', {
                method: 'POST',
                headers,
                credentials: 'include',
              });
              
              // Sign out from Supabase AFTER backend logout
              await supabase.auth.signOut();
              
              // Redirect to home
              window.location.href = '/';
            } catch (error) {
              console.error('Error logging out:', error);
              // Force redirect even if there's an error
              window.location.href = '/';
            }
          }}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '8px',
            transition: 'all 200ms ease',
            fontWeight: '500',
            backgroundColor: 'transparent',
            color: '#ef4444',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)';
            e.currentTarget.style.color = '#f87171';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#ef4444';
          }}
          data-testid="menu-logout"
        >
          <LogOut style={{ width: '16px', height: '16px' }} />
          Cerrar Sesión
        </button>
        
        <div style={{
          fontSize: '12px',
          color: '#475569',
          textAlign: 'center',
          marginTop: '16px'
        }}>
          Next Point v1.0
        </div>
      </div>
    </div>
  );
}
